import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import organizationService from '../../services/organizationService';
import memberService from '../../services/memberService';
import servePositionService from '../../services/servePositionService';
import { addNotification } from '../../store/slices/uiSlice';
import './GroupMembersModal.css';

/** 領導層牧養崗位。小組長 is the older name for 組長. */
const LEADERSHIP_DISPLAY = {
  組長: '組長',
  小組長: '組長',
  核心: '核心',
  核心成員: '核心',
  區長: '區長',
  區牧: '區牧',
  總區牧: '總區牧',
  實習區長: '實習區長',
  實習區牧: '實習區牧',
  實習總區牧: '實習總區牧',
};
const LEADERSHIP_ORDER = ['組長', '核心', '區長', '區牧', '總區牧', '實習區長', '實習區牧', '實習總區牧'];
/** These leadership roles cannot also be 核心 */
const LEADER_NO_CORE = new Set(['組長', '區長', '區牧', '總區牧', '實習區長', '實習區牧', '實習總區牧']);

const GroupMembersModal = ({ group, onClose, onMembersChanged }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const autocompleteRef = useRef(null);
  const [assignments, setAssignments] = useState([]);
  const [members, setMembers] = useState([]);
  const [leadershipByMember, setLeadershipByMember] = useState({});
  const [coreMemberIds, setCoreMemberIds] = useState(() => new Set());
  const [loadingAssignments, setLoadingAssignments] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [newMemberId, setNewMemberId] = useState('');
  const [newRole, setNewRole] = useState('core');
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [pendingRoleChanges, setPendingRoleChanges] = useState({});
  const [error, setError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const roleOptions = [
    { value: 'core', label: t('organization.membersModal.roleCore') },
    { value: 'full', label: t('organization.membersModal.roleFull') },
    { value: 'associate', label: t('organization.membersModal.roleAssociate') }
  ];

  const isLeaderNoCore = (memberId) => {
    const labels = leadershipByMember[memberId] || [];
    return labels.some((label) => LEADER_NO_CORE.has(label));
  };

  const roleOptionsForMember = (memberId) =>
    roleOptions.map((option) =>
      option.value === 'core' && isLeaderNoCore(memberId)
        ? { ...option, disabled: true }
        : option
    );

  const loadAssignments = async () => {
    setLoadingAssignments(true);
    setError('');
    try {
      const data = await organizationService.getGroupMembers(group.id);
      setAssignments(data || []);
    } catch (err) {
      console.error('Error loading group members:', err);
      setError(t('organization.error'));
    } finally {
      setLoadingAssignments(false);
    }
  };

  const loadMembers = async () => {
    setLoadingMembers(true);
    try {
      const [data, assignmentRes, groups] = await Promise.all([
        memberService.getMembers({ limit: 1000 }),
        servePositionService.getAssignments({ status: 'active', limit: 1000 }),
        organizationService.getGroups().catch(() => []),
      ]);
      const groupLists = await Promise.all(
        (groups || []).map((groupItem) =>
          organizationService.getGroupMembers(groupItem.id).catch(() => [])
        )
      );
      const coreIds = new Set();
      groupLists.flat().forEach((item) => {
        if (item?.role === 'core' && item.member_id) coreIds.add(item.member_id);
      });
      setCoreMemberIds(coreIds);
      const byMember = {};
      (assignmentRes.items || []).forEach((item) => {
        const label = LEADERSHIP_DISPLAY[item.position_name];
        if (!label || !item.member_id) return;
        if (!byMember[item.member_id]) byMember[item.member_id] = [];
        if (!byMember[item.member_id].includes(label)) byMember[item.member_id].push(label);
      });
      Object.keys(byMember).forEach((id) => {
        byMember[id].sort((a, b) => LEADERSHIP_ORDER.indexOf(a) - LEADERSHIP_ORDER.indexOf(b));
      });
      setLeadershipByMember(byMember);
      setMembers(data.data || []);
    } catch (err) {
      console.error('Error loading members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (!group) return;
    loadAssignments();
  }, [group]);

  useEffect(() => {
    loadMembers();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (autocompleteRef.current && !autocompleteRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddAssignment = async () => {
    if (!newMemberId) return;
    const memberId = parseInt(newMemberId, 10);
    const roleToSave = newRole === 'core' && isLeaderNoCore(memberId) ? 'full' : newRole;
    if (newRole === 'core' && isLeaderNoCore(memberId)) {
      setError(t('organization.membersModal.coreForbiddenForLeader'));
      dispatch(addNotification({
        type: 'error',
        message: t('organization.membersModal.coreForbiddenForLeader')
      }));
      return;
    }
    setSavingAssignment(true);
    setError('');
    try {
      await organizationService.assignMemberToGroup(group.id, {
        group_id: group.id,
        member_id: memberId,
        role: roleToSave
      });
      setNewMemberId('');
      setNewRole('core');
      setMemberSearch('');
      await loadAssignments();
      onMembersChanged?.();
      dispatch(addNotification({
        type: 'success',
        message: t('organization.membersModal.addSuccess')
      }));
    } catch (err) {
      console.error('Error assigning member:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t('organization.membersModal.addError');
      setError(msg);
      dispatch(addNotification({
        type: 'error',
        message: msg
      }));
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleRoleChange = (assignmentId, nextRole, originalRole, memberId) => {
    if (nextRole === 'core' && isLeaderNoCore(memberId)) {
      dispatch(addNotification({
        type: 'error',
        message: t('organization.membersModal.coreForbiddenForLeader')
      }));
      return;
    }
    if (nextRole === originalRole) {
      setPendingRoleChanges(prev => {
        const updated = { ...prev };
        delete updated[assignmentId];
        return updated;
      });
    } else {
      setPendingRoleChanges(prev => ({
        ...prev,
        [assignmentId]: nextRole
      }));
    }
  };

  const handleSaveAll = async () => {
    const pendingIds = Object.keys(pendingRoleChanges);
    if (pendingIds.length === 0) return;

    setSavingAssignment(true);
    setError('');
    try {
      for (const assignmentId of pendingIds) {
        await organizationService.updateMemberAssignment(assignmentId, { role: pendingRoleChanges[assignmentId] });
      }
      setPendingRoleChanges({});
      await loadAssignments();
      onMembersChanged?.();
      dispatch(addNotification({
        type: 'success',
        message: t('organization.membersModal.roleSuccess')
      }));
    } catch (err) {
      console.error('Error updating roles:', err);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : t('organization.membersModal.roleError');
      setError(msg);
      dispatch(addNotification({
        type: 'error',
        message: msg
      }));
    } finally {
      setSavingAssignment(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    if (!window.confirm(t('organization.confirmRemoveMember'))) {
      return;
    }

    setSavingAssignment(true);
    setError('');
    try {
      await organizationService.removeMemberFromGroup(assignmentId);
      await loadAssignments();
      onMembersChanged?.();
    } catch (err) {
      console.error('Error removing assignment:', err);
      setError(t('organization.membersModal.removeError'));
    } finally {
      setSavingAssignment(false);
    }
  };

  const assignedMemberIds = new Set(assignments.map(item => item.member_id));
  const availableMembers = members.filter(member => !assignedMemberIds.has(member.id));

  const getServePositions = (member) => {
    const fromAssignments = leadershipByMember[member?.id] || [];
    const fromProfile = (member?.serve_post_items || '')
      .split(',')
      .map((s) => LEADERSHIP_DISPLAY[s.trim()])
      .filter(Boolean);
    const merged = [];
    [...fromAssignments, ...fromProfile].forEach((label) => {
      if (!merged.includes(label)) merged.push(label);
    });
    const isLeader = merged.some((label) => LEADER_NO_CORE.has(label));
    if (
      !isLeader &&
      (coreMemberIds.has(member?.id) || member?.group_role === 'core') &&
      !merged.includes('核心')
    ) {
      merged.push('核心');
    }
    return merged
      .filter((label) => !(isLeader && label === '核心'))
      .sort((a, b) => LEADERSHIP_ORDER.indexOf(a) - LEADERSHIP_ORDER.indexOf(b));
  };

  /** 黃偉豪 [區長] 000200 — worship number is not wrapped in parentheses */
  const formatMemberLabel = (member) => {
    const name = member?.chi_name || '';
    const worshipNo = member?.worship_id || '';
    const positions = getServePositions(member);
    const role = positions.length > 0 ? `[${positions.join(', ')}]` : '';
    return [name, role, worshipNo].filter(Boolean).join(' ');
  };
  
  // Filter members based on search
  const filteredMembers = availableMembers.filter(member => {
    const searchLower = memberSearch.toLowerCase();
    const positions = getServePositions(member).join(' ').toLowerCase();
    return (
      member.chi_name?.toLowerCase().includes(searchLower) ||
      member.worship_id?.toLowerCase().includes(searchLower) ||
      member.eng_name?.toLowerCase().includes(searchLower) ||
      positions.includes(searchLower)
    );
  });

  const handleSelectMember = (member) => {
    setNewMemberId(member.id.toString());
    setMemberSearch(formatMemberLabel(member));
    setShowDropdown(false);
    if (isLeaderNoCore(member.id) && newRole === 'core') {
      setNewRole('full');
    }
  };

  const handleSearchChange = (e) => {
    setMemberSearch(e.target.value);
    setNewMemberId('');
    setShowDropdown(true);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('organization.membersModal.title', { group: group.name })}</h2>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          <div className="member-summary">
            <p>{t('organization.membersModal.summaryCore', { count: assignments.filter(a => a.role === 'core').length })}</p>
            <p>{t('organization.membersModal.summaryFull', { count: assignments.filter(a => a.role === 'full').length })}</p>
            <p>{t('organization.membersModal.summaryAssociate', { count: assignments.filter(a => a.role === 'associate').length })}</p>
          </div>
          <div className="form-group">
            <label>{t('organization.membersModal.addLabel')}</label>
            <div className="member-form-grid">
              <div className="autocomplete-container" ref={autocompleteRef}>
                <input
                  type="text"
                  className="form-control"
                  value={memberSearch}
                  onChange={handleSearchChange}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={loadingMembers ? t('organization.membersModal.loadingMembers') : t('organization.membersModal.searchPlaceholder')}
                  disabled={loadingMembers || savingAssignment}
                />
                {showDropdown && memberSearch && filteredMembers.length > 0 && (
                  <div className="autocomplete-dropdown">
                    {filteredMembers.slice(0, 10).map(member => {
                      const positions = getServePositions(member);
                      return (
                        <div
                          key={member.id}
                          className="autocomplete-item"
                          onClick={() => handleSelectMember(member)}
                        >
                          <span className="autocomplete-name">{member.chi_name}</span>
                          {positions.length > 0 && (
                            <span className="autocomplete-role">[{positions.join(', ')}]</span>
                          )}
                          {member.worship_id && (
                            <span className="autocomplete-id">{member.worship_id}</span>
                          )}
                        </div>
                      );
                    })}
                    {filteredMembers.length > 10 && (
                      <div className="autocomplete-more">
                        {t('organization.membersModal.moreMembers', { count: filteredMembers.length - 10 })}
                      </div>
                    )}
                  </div>
                )}
                {showDropdown && memberSearch && filteredMembers.length === 0 && !loadingMembers && (
                  <div className="autocomplete-dropdown">
                    <div className="autocomplete-empty">{t('organization.membersModal.noMembers')}</div>
                  </div>
                )}
              </div>
              <select
                className="form-control"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                disabled={savingAssignment}
                title={newMemberId && isLeaderNoCore(parseInt(newMemberId, 10))
                  ? t('organization.membersModal.coreForbiddenForLeader')
                  : undefined}
              >
                {roleOptionsForMember(newMemberId ? parseInt(newMemberId, 10) : null).map(option => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddAssignment}
                disabled={!newMemberId || savingAssignment}
              >
                <i className="fas fa-user-plus"></i> {t('organization.membersModal.addButton')}
              </button>
            </div>
          </div>
          {error && <p className="modal-error">{error}</p>}
          <div className="member-list">
            {loadingAssignments ? (
              <p>{t('organization.membersModal.loadingAssignments')}</p>
            ) : assignments.length === 0 ? (
              <p className="empty-state">{t('organization.membersModal.empty')}</p>
            ) : (
              assignments.map(assignment => {
                const hasPendingChange = pendingRoleChanges.hasOwnProperty(assignment.id);
                const currentRole = hasPendingChange ? pendingRoleChanges[assignment.id] : assignment.role;
                return (
                  <div key={assignment.id} className={`member-row ${hasPendingChange ? 'member-row-modified' : ''}`}>
                    <div>
                      <div className="member-name">{assignment.member?.chi_name || `會員 ${assignment.member_id}`}</div>
                      <div className="member-sub">{assignment.member?.worship_id ? t('organization.membersModal.worshipId', { id: assignment.member.worship_id }) : t('organization.membersModal.worshipMissing')}</div>
                    </div>
                    <div className="member-row-actions">
                      <select
                        className="form-control"
                        value={currentRole}
                        onChange={(e) => handleRoleChange(
                          assignment.id,
                          e.target.value,
                          assignment.role,
                          assignment.member_id
                        )}
                        disabled={savingAssignment}
                        title={isLeaderNoCore(assignment.member_id)
                          ? t('organization.membersModal.coreForbiddenForLeader')
                          : undefined}
                      >
                      {roleOptionsForMember(assignment.member_id).map(option => (
                        <option key={option.value} value={option.value} disabled={option.disabled}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleRemoveAssignment(assignment.id)}
                      disabled={savingAssignment}
                    >
                      <i className="fas fa-trash"></i> {t('organization.membersModal.remove')}
                    </button>
                  </div>
                </div>
              );
              })
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveAll}
            disabled={Object.keys(pendingRoleChanges).length === 0 || savingAssignment}
          >
            <i className="fas fa-save"></i> {t('organization.membersModal.saveAll')}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {t('organization.membersModal.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupMembersModal;
